import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { shippingService } from "../services/dataService";
import { Firm, ShipmentDetail, ShipmentListItem, ShipmentStatus as S, ShippingStats } from "../types";
import { turkishIncludes } from "../utils/formatters";
import { usePermissions } from "../context/PermissionContext";
import { ShAge, SH_STATUS_CLASS, SH_STATUS_LABEL, shAge, shErr, shFmtDate, shFmtQty, shIsOpen } from "../utils/shipping";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import ShipmentDrawer from "../components/ShipmentDrawer";
import ShipmentModal from "../components/ShipmentModal";
import ShipmentActionModal, { ShipmentAction } from "../components/ShipmentActionModal";
import ShipmentPrintModal from "../components/ShipmentPrintModal";
import { FirmPickerField, firmDirectory, firmHue, firmInitials } from "../components/FirmPicker";
import "./PurchaseOrders.css";
import "./Shipping.css";

export { SH_STATUS_LABEL };

/**
 * Sevkiyat — tek rota (/shipping/*):
 *   /shipping           liste
 *   /shipping/<id>      liste + sevkiyat kartı (çekmece) — bildirim ve sipariş detayı bağlantıları buraya gelir
 *   /shipping/new       yeni sevkiyat modalı açılır, adres /shipping'e döner (?firmId=&orderId= ön seçim)
 * Yeni sevkiyat ve taslak düzenleme sayfa değil modaldir (ShipmentModal). Tüm işlemler ve onaylar bu sayfadadır.
 */

type Chip = "open" | "draft" | "shipped" | "delivered" | "cancelled" | "all";
const CHIPS: [Chip, string][] = [["open", "Açık"], ["draft", "Taslak"], ["shipped", "Yolda"], ["delivered", "Teslim edildi"], ["cancelled", "İptal"], ["all", "Hepsi"]];
const chipMatch = (c: Chip, s: S) =>
  c === "all" ? true : c === "open" ? shIsOpen(s) : c === "draft" ? s === S.Draft : c === "shipped" ? s === S.Shipped
    : c === "delivered" ? s === S.Delivered : s === S.Cancelled;

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 4v5h-5" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>,
  filterOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h16l-6 7v6l-4 2v-8z" /><path d="m3 3 18 18" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>,
  receive: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M4 19h16" /></svg>,
  warn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 20h20z" /><path d="M12 10v4M12 17.5v.5" /></svg>,
  print: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><path d="M6 14h12v7H6z" /></svg>,
};

const Shipping: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const seg = (useParams()["*"] || "").split("/")[0]; // "" | "new" | <id>
  const drawerId = seg && seg !== "new" ? seg : null;
  const { has, hasModule, loaded: permsLoaded } = usePermissions();
  const canUpdate = has("shipping:update");

  const [stats, setStats] = useState<ShippingStats | null>(null);
  const [shipments, setShipments] = useState<ShipmentListItem[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [initial, setInitial] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [loaded, setLoaded] = useState(false); // en az bir kez başarıyla yüklendi
  const [q, setQ] = useState("");
  const [chip, setChip] = useState<Chip>("open");
  const [lateOnly, setLateOnly] = useState(false);
  const [firmFilter, setFirmFilter] = useState("");
  const [modal, setModal] = useState<{ open: boolean; shipment: ShipmentDetail | null; firmId?: string; orderIds?: string[] }>({ open: false, shipment: null });
  const [refreshKey, setRefreshKey] = useState(0);
  const [action, setAction] = useState<{ kind: ShipmentAction; shipment: ShipmentDetail } | null>(null);
  const [printFor, setPrintFor] = useState<{ id: string; number: string } | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "info" | "success" | "warning" | "error" }>({ isOpen: false, message: "", type: "info" });
  const [confirm, setConfirm] = useState<{ isOpen: boolean; title: string; message: string; type: "info" | "warning" | "danger"; confirmText: string; onConfirm: () => void }>({ isOpen: false, title: "", message: "", type: "info", confirmText: "Onayla", onConfirm: () => {} });
  const ok = (message: string) => setAlert({ isOpen: true, message, type: "success" });
  const fail = (message: string) => setAlert({ isOpen: true, message, type: "error" });
  const warn = (message: string) => setAlert({ isOpen: true, message, type: "warning" });
  const closeConfirm = () => setConfirm((c) => ({ ...c, isOpen: false }));

  const load = useCallback(async (forceFirms = false) => {
    setLoadError("");
    try {
      const [s, list, open, f] = await Promise.all([
        shippingService.stats(),
        shippingService.shipments(),
        // Liste sunucuda son 1000 kayıtla sınırlı: eski ama hâlâ yolda olan sevkiyatlar KPI ve şeritten düşmesin
        shippingService.shipments({ status: S.Shipped }),
        // Firma listesi yalnız süzgeç ve modal içindir: alınamazsa sevkiyat listesi yine açılır
        firmDirectory.load(forceFirms).catch(() => firmDirectory.peek() ?? []),
      ]);
      const seen = new Set(list.map((x) => x.shipmentId));
      setStats(s); setShipments(list.concat(open.filter((x) => !seen.has(x.shipmentId)))); setFirms(f); setLoaded(true);
    } catch (e: any) { setLoadError(shErr(e, "Sevkiyat verileri yüklenemedi.")); }
    finally { setInitial(false); }
  }, []);
  // Yetkiler gelmeden yüklenmez: /new derin bağlantısındaki yetki kontrolü ilk yükleme bitince yapılır
  useEffect(() => { if (permsLoaded) load(); }, [load, permsLoaded]);

  const refresh = async () => { setBusy(true); await load(true); setRefreshKey((k) => k + 1); setBusy(false); };

  // /shipping/new → modal; ön seçim modal durumuna alınır, adres listeye döner (yenilemede modal tekrar açılmaz)
  useEffect(() => {
    if (seg !== "new" || initial) return;
    if (has("shipping:create")) setModal({ open: true, shipment: null, firmId: searchParams.get("firmId")?.toLowerCase() || undefined, orderIds: Array.from(new Set(searchParams.getAll("orderId").map((x) => x.toLowerCase()))) }); // API kimlikleri küçük harf döner
    else warn("Sevkiyat oluşturma yetkiniz yok.");
    navigate("/shipping", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seg, initial]);

  const openDrawer = (id: string) => navigate(`/shipping/${id}`, { state: { fromList: true } });
  const closeDrawer = () => ((location.state as any)?.fromList ? navigate(-1) : navigate("/shipping", { replace: true }));

  // ---- Türetilmiş ----
  const counts = useMemo(() => Object.fromEntries(CHIPS.map(([k]) => [k, shipments.filter((s) => chipMatch(k, s.status)).length])) as Record<Chip, number>, [shipments]);
  const ages = useMemo(() => new Map<string, ShAge>(shipments.map((s) => [s.shipmentId, shAge(s.shippedAt, s.status)])), [shipments]);
  const lateCount = useMemo(() => shipments.filter((s) => ages.get(s.shipmentId)!.stuck).length, [shipments, ages]);
  const rows = useMemo(() => {
    const terms = q.trim().split(/\s+/).filter(Boolean);
    const rank = (s: ShipmentListItem) => (ages.get(s.shipmentId)!.stuck ? 0 : s.status === S.Shipped ? 1 : s.status === S.Draft ? 2 : s.status === S.Delivered ? 3 : 4);
    return shipments.filter((s) => {
      if (!chipMatch(chip, s.status)) return false;
      if (lateOnly && !ages.get(s.shipmentId)!.stuck) return false;
      if (firmFilter && s.firmId !== firmFilter) return false;
      const hay = [s.number, s.firmName, s.firmCode || "", s.waybillNo || "", s.vehiclePlate || "", s.carrier || "", s.models || "", s.createdBy || ""];
      return terms.every((t) => hay.some((h) => turkishIncludes(h, t)));
    }).sort((a, b) => {
      const ra = rank(a), rb = rank(b);
      if (ra !== rb) return ra - rb;
      if (ra === 0) return (a.shippedAt || "") < (b.shippedAt || "") ? -1 : 1; // en uzun süredir yolda olan önce
      return (a.shippedAt || a.shipmentDate) < (b.shippedAt || b.shipmentDate) ? 1 : -1;
    });
  }, [shipments, ages, chip, lateOnly, firmFilter, q]);
  const attention = useMemo(() => shipments
    .map((s) => ({ s, a: ages.get(s.shipmentId)! }))
    .filter((x) => x.a.stuck)
    .sort((x, y) => (y.a.days ?? 0) - (x.a.days ?? 0))
    .slice(0, 6), [shipments, ages]);
  const listTotals = useMemo(() => {
    let qty = 0, pk = 0;
    rows.forEach((s) => { if (s.status !== S.Cancelled) { qty += s.totalQuantity; pk += s.packageCount ?? 0; } });
    return { qty, pk };
  }, [rows]);
  const filterOn = !!q || chip !== "open" || lateOnly || !!firmFilter;
  const resetFilters = () => { setQ(""); setChip("open"); setLateOnly(false); setFirmFilter(""); };

  // ---- İşlemler (tek yerde: onay, bildirim, yenileme) ----
  const after = async (msg?: string) => { await load(); setRefreshKey((k) => k + 1); if (msg) ok(msg); };
  const fetchDetail = async (id: string) => {
    setRowBusy(id);
    try { return await shippingService.shipment(id); }
    catch (e: any) {
      if (e?.response?.status === 404) { fail("Sevkiyat bulunamadı; silinmiş olabilir. Liste yenilendi."); load(); }
      else fail(shErr(e, "Sevkiyat yüklenemedi."));
      return null;
    }
    finally { setRowBusy(null); }
  };
  const editShipment = async (x: string | ShipmentDetail) => {
    const d = typeof x === "string" ? await fetchDetail(x) : x;
    if (!d) return;
    if (d.status !== S.Draft) return warn("Yalnız taslak sevkiyatlar düzenlenebilir.");
    setModal({ open: true, shipment: d });
  };
  const startAction = async (kind: ShipmentAction, x: string | ShipmentDetail) => {
    const d = typeof x === "string" ? await fetchDetail(x) : x;
    if (d) setAction({ kind, shipment: d });
  };
  const shipShipment = (x: string | ShipmentDetail) => startAction("ship", x);
  const deliverShipment = (x: string | ShipmentDetail) => startAction("deliver", x);
  const cancelShipment = (x: string | ShipmentDetail) => startAction("cancel", x);
  const deleteShipment = (o: { shipmentId: string; number: string }) => setConfirm({
    isOpen: true, type: "danger", title: "Taslağı sil", confirmText: "Sil",
    message: `${o.number} taslak sevkiyatı kalıcı olarak silinecek. Satırlardaki siparişler yeniden sevk edilebilir olur.`,
    onConfirm: async () => {
      closeConfirm(); setBusy(true);
      try {
        await shippingService.remove(o.shipmentId);
        if (drawerId === o.shipmentId) closeDrawer();
        await after("Taslak silindi.");
      } catch (e) { fail(shErr(e, "Silinemedi.")); }
      finally { setBusy(false); }
    },
  });
  const printShipment = (o: { shipmentId: string; number: string }) => setPrintFor({ id: o.shipmentId, number: o.number });
  const onActionDone = (message: string) => { setAction(null); after(message); };
  const onModalSaved = async (r: { shipmentId: string; number?: string; thenShip: boolean }) => {
    setModal({ open: false, shipment: null });
    await load(); setRefreshKey((k) => k + 1);
    if (drawerId !== r.shipmentId) navigate(`/shipping/${r.shipmentId}`, { state: { fromList: true } });
    if (r.thenShip) {
      // Modal /ship çağırmaz: kayıt taslak olarak durur, sevk onayı işlem penceresinde alınır
      const d = await fetchDetail(r.shipmentId);
      if (d) setAction({ kind: "ship", shipment: d });
      else warn(`${r.number ?? "Sevkiyat"} taslak olarak kaydedildi; sevk penceresi açılamadı, karttan "Sevk et" ile devam edin.`);
    } else ok(`${r.number ?? "Sevkiyat"} taslak olarak kaydedildi.`);
  };

  if (initial) return <PageLoader message="Sevkiyat yükleniyor..." />;

  return (
    <div className="po-page">
      <header className="po-head">
        <div>
          <span className="po-kicker">Lojistik</span>
          <h1>Sevkiyat</h1>
          <p>Sevk irsaliyesi, araç ve şoför bilgisi, teslimat onayı. {hasModule("stock") ? "Sevk edilen miktar üretim stoğundan çıkış olarak işlenir; iptal geri alır." : "Stok modülü kapalı; yalnız sevk miktarı izlenir."}</p>
        </div>
        <div className="po-head-actions">
          <button type="button" className="po-btn" onClick={refresh} disabled={busy}><span className={busy ? "po-spin-icon" : ""} style={{ display: "inline-flex" }}>{I.refresh}</span> Yenile</button>
          {has("shipping:create") && <button type="button" className="po-btn primary" onClick={() => setModal({ open: true, shipment: null })}>{I.plus} Yeni sevkiyat</button>}
        </div>
      </header>

      {loadError && <div className="po-note err" role="alert">{loadError} <button type="button" className="po-link" onClick={refresh}>Yeniden dene</button></div>}

      {stats && (
        <section className="po-kpis">
          <div className="po-kpi navy"><span className="l">Yolda</span><span className="v">{stats.inTransitCount}<small> sevkiyat</small></span><span className="s"><b>{stats.inTransitPackages}</b> koli · <b>{stats.draftCount}</b> taslak ayrıca</span></div>
          <button type="button" className={`po-kpi ${lateCount > 0 ? "err" : "ok"} ${lateOnly ? "on" : ""}`} aria-pressed={lateOnly} onClick={() => setLateOnly((v) => !v)}>
            <span className="l">Teslim onayı geciken</span><span className="v">{lateCount}</span><span className="s">{lateCount > 0 ? "3 günden uzun süredir yolda · süzmek için tıklayın" : "gecikme yok"}</span>
          </button>
          <div className="po-kpi"><span className="l">Sevk bekleyen sipariş</span><span className="v">{stats.awaitingOrders}</span><span className="s">tamamlanmış, tam sevk edilmemiş</span></div>
          <div className="po-kpi ok"><span className="l">Son 30 gün teslim</span><span className="v">{stats.deliveredLast30}</span><span className="s">teslim onaylanan sevkiyat</span></div>
        </section>
      )}

      {attention.length > 0 && (
        <div className="po-section">
          <h2>Teslim onayı bekleyen sevkiyatlar</h2>
          <div className="po-upcoming">
            {attention.map(({ s, a }) => (
              <button type="button" key={s.shipmentId} className={`po-up ${a.state}`} onClick={() => openDrawer(s.shipmentId)}>
                <span className="po-mark" style={{ ["--po-hue" as any]: firmHue(s.firmName) }}>{firmInitials(s.firmName)}</span>
                <div><b>{s.firmName}</b><small>{a.text} · {s.number}{s.vehiclePlate ? ` · ${s.vehiclePlate}` : ""}</small></div>
                <span className="amt">{shFmtQty(s.totalQuantity)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <section className={`po-card ${busy ? "dim" : ""}`}>
        <div className="po-toolbar">
          <div className="po-search">{I.search}<input placeholder="Sevkiyat no, irsaliye no, firma, plaka, nakliyeci veya model ara" aria-label="Sevkiyat ara" value={q} onChange={(e) => setQ(e.target.value)} />{q && <button type="button" className="po-ib" aria-label="Temizle" onClick={() => setQ("")}>{I.close}</button>}</div>
          <div className="po-chipgroup" role="group" aria-label="Durum">
            {CHIPS.map(([k, l]) => <button type="button" key={k} className={chip === k ? "on" : ""} aria-pressed={chip === k} onClick={() => setChip(k)}>{l}<i>{counts[k]}</i></button>)}
          </div>
          {(lateCount > 0 || lateOnly) && <button type="button" className={`po-chip danger ${lateOnly ? "on" : ""}`} aria-pressed={lateOnly} onClick={() => setLateOnly((v) => !v)}>{I.warn} Onayı geciken <i>{lateCount}</i></button>}
          <FirmPickerField variant="toolbar" value={firmFilter} firms={firms} nullLabel="Tüm firmalar" allowCreate={false} title="Firmaya göre süz" onChange={(f) => setFirmFilter(f?.firmId || "")} />
          {filterOn && <button type="button" className="po-clear" onClick={resetFilters}>{I.filterOff} Temizle</button>}
        </div>
        <div className="po-card-head">
          <span><b>{rows.length}</b> sevkiyat</span>
          <span className="po-hint">{rows.length > 0 && <><b>{shFmtQty(listTotals.qty)}</b> birim{listTotals.pk > 0 && <> · <b>{listTotals.pk}</b> koli</>}</>}</span>
        </div>
        {rows.length === 0 ? (
          !loaded ? (
            <div className="po-blank"><span className="po-blank-icon">{I.warn}</span><b>Sevkiyatlar yüklenemedi</b>Bağlantıyı kontrol edip yeniden deneyin.
              <button type="button" className="po-btn" onClick={refresh} disabled={busy}>{I.refresh} Yeniden dene</button></div>
          ) : shipments.length === 0 ? (
            <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Henüz sevkiyat yok</b>İlk sevkiyatı oluşturun; sevk edildiğinde teslimat buradan izlenir.
              {has("shipping:create") && <button type="button" className="po-btn primary" onClick={() => setModal({ open: true, shipment: null })}>{I.plus} İlk sevkiyatı oluştur</button>}</div>
          ) : (
            <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Süzgeçle eşleşen sevkiyat yok</b>Süzgeci değiştirin.
              <button type="button" className="po-btn" onClick={resetFilters}>Süzgeci sıfırla</button></div>
          )
        ) : (
          <div className="po-table-wrap">
            <table className="po-table">
              <thead><tr><th>Sevkiyat</th><th className="po-hide-sm">Firma</th><th>Sevk / Teslim</th><th className="po-hide-md">Araç</th><th className="po-hide-lg">İrsaliye</th><th>Durum</th><th className="num">Miktar</th><th className="act" /></tr></thead>
              <tbody>
                {rows.map((s) => {
                  const id = s.shipmentId;
                  const a = ages.get(id)!;
                  return (
                    <tr key={id} className={[drawerId === id ? "on" : "", a.state === "late" ? "late" : "", s.status === S.Cancelled ? "off" : ""].join(" ")} onClick={() => openDrawer(id)}>
                      <td><div className="po-row">
                        <span className={`po-mark ${s.status === S.Cancelled ? "off" : ""}`} style={{ ["--po-hue" as any]: firmHue(s.firmName) }}>{firmInitials(s.firmName)}</span>
                        <div><div className="po-code">{s.number}</div><div className="po-sub">{s.lineCount} sipariş · {shFmtDate(s.shipmentDate)}</div><div className="po-sub po-only-sm">{s.firmName}</div></div>
                      </div></td>
                      <td className="po-hide-sm"><div className="po-name">{s.firmName}</div><div className="po-sub po-hide-md sh-models">{s.firmCode ? `${s.firmCode} · ` : ""}{s.models || "—"}</div></td>
                      <td className={`po-due ${a.state}`}>
                        {s.status === S.Shipped ? <>{shFmtDate(s.shippedAt)}<div className="po-sub">{a.text}</div></>
                          : s.status === S.Delivered ? <>{shFmtDate(s.deliveredAt)}<div className="po-sub">teslim · {s.receivedBy || "—"}</div></>
                            : s.status === S.Draft ? <span className="po-sub">planlanan {shFmtDate(s.shipmentDate)}</span> : "—"}
                      </td>
                      <td className="po-hide-md">
                        {s.vehiclePlate || s.carrier ? <>{s.vehiclePlate && <span className="po-codepill sh-plate">{s.vehiclePlate}</span>}{s.carrier && <div className="po-sub">{s.carrier}</div>}</> : "—"}
                      </td>
                      <td className="po-hide-lg">{s.waybillNo ? <span className="po-codepill sh-plate">{s.waybillNo}</span> : "—"}</td>
                      <td><span className={`po-st ${SH_STATUS_CLASS[s.status]}`}>{SH_STATUS_LABEL[s.status]}</span></td>
                      <td className="num"><span className="po-amt">{shFmtQty(s.totalQuantity)}</span>{s.packageCount != null && <div className="po-sub">{s.packageCount} koli</div>}</td>
                      <td className="act"><div className="po-rowacts" onClick={(e) => e.stopPropagation()}>
                        {s.status === S.Draft && canUpdate && <>
                          <button type="button" className="po-ib" title="Düzenle" aria-label={`${s.number} düzenle`} disabled={rowBusy === id} onClick={() => editShipment(id)}>{I.edit}</button>
                          <button type="button" className="po-ib" title="Sevk et" aria-label={`${s.number} sevk et`} disabled={rowBusy === id} onClick={() => shipShipment(id)}>{I.send}</button>
                        </>}
                        {s.status === S.Shipped && canUpdate && <button type="button" className="po-btn small" disabled={rowBusy === id} onClick={() => deliverShipment(id)}>{I.receive} Teslim edildi</button>}
                        <button type="button" className="po-ib" title="Sevkiyat kartı" aria-label={`${s.number} sevkiyat kartı`} onClick={() => openDrawer(id)}>{I.eye}</button>
                      </div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Katmanlar */}
      {drawerId && (
        <ShipmentDrawer shipmentId={drawerId} preview={shipments.find((s) => s.shipmentId === drawerId)} refreshKey={refreshKey} busy={busy}
          escDisabled={confirm.isOpen || modal.open || !!action || !!printFor}
          onClose={closeDrawer} onEdit={editShipment} onShip={shipShipment} onDeliver={deliverShipment} onCancel={cancelShipment}
          onDelete={deleteShipment} onPrint={printShipment} />
      )}
      {modal.open && (
        <ShipmentModal shipment={modal.shipment} initialFirmId={modal.firmId} initialOrderIds={modal.orderIds} firms={firms}
          onClose={() => setModal({ open: false, shipment: null })} onSaved={onModalSaved} />
      )}
      {action && <ShipmentActionModal shipment={action.shipment} action={action.kind} onClose={() => setAction(null)} onDone={onActionDone} />}
      {printFor && <ShipmentPrintModal shipmentId={printFor.id} number={printFor.number} onClose={() => setPrintFor(null)} />}
      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert((a) => ({ ...a, isOpen: false }))} />
      <ConfirmModal isOpen={confirm.isOpen} title={confirm.title} message={confirm.message} type={confirm.type} confirmText={confirm.confirmText} cancelText="Vazgeç" onConfirm={confirm.onConfirm} onCancel={closeConfirm} />
    </div>
  );
};

export default Shipping;
